import React, { memo, useCallback } from 'react';
import { CustomTableCell } from '../../styles';
import { CustomTableCellForAccount, CustomTableCellRedirectToVoucher } from './tableCell';
import { TableRow, Button, Tooltip, Box } from '@mui/material';
import { formatNumber, TRANSACTION_CONFIG } from '../../../../utils/common';
import { useSetOffModal, SetOffModal } from '../../../../features/LedgerTransactions';
import { formatUnixTimestamp } from '../../../../library';

export const CustomTableRow = memo(
  ({
    data,
    index,
    handleTransationModal,
    accountDetails,
    accountTypeFlag,
    showLinkedInvoiceVsReceipt = false,
    fetchData,
    filters,
    rowsperpage,
    openingBalanceSec,
    headCell = [],
  }) => {
    const { isOpen, transactionData, openModal: openSetOffModal, closeModal: closeSetOffModal } = useSetOffModal();

    const getCellWidth = (cellId) => {
      const cell = headCell.find((hc) => hc.id === cellId);
      if (cell) {
        const width = cell.width;
        return {
          width,
          minWidth: cell.minWidth || width,
          maxWidth: width,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        };
      }
      return {};
    };

    const handleTransactionClick = useCallback(() => {
      handleTransationModal(data.oppositeaccountid);
    }, [data]);

    const handleSetOffClick = useCallback(() => {
      const voucherType = data.transactiontype;
      const config = TRANSACTION_CONFIG[voucherType] || undefined;
      let voucherId = config?.getTransactionId(data);
      let voucherNumber = data.vouchernumber;
      let accountid = data.accountid;
      if (data.transactiontype === 'OpeningBalance') {
        voucherId = data.transactionid;
        voucherNumber = 'OpeningBalance';
        accountid = data.accountid;
      }
      openSetOffModal(voucherType, voucherId, voucherNumber, accountid);
    }, [data, openSetOffModal]);

    const hasDebitValue = accountTypeFlag === 1 && Number(data.totaldebitamount || 0);
    const hasCreditValue = accountTypeFlag === 2 && Number(data?.totalcreditamount || 0);
    const isOpeningBalance = data.transactiontype === 'OpeningBalance';
    const isOpeningBalanceFilter = isOpeningBalance && openingBalanceSec && Number(filters?.start) === Number(openingBalanceSec);
    const isOpeningBalanceWithValidBalance =
      isOpeningBalanceFilter &&
      ((accountTypeFlag === 1 && Number(data?.balance || 0) > 0) || (accountTypeFlag === 2 && Number(data?.balance || 0) > 0));
    const canRenderSetOffButton = (!isOpeningBalance && (hasDebitValue || hasCreditValue)) || isOpeningBalanceWithValidBalance;

    return (
      <>
        <TableRow className="noTableCellBorder" key={index + 'r'}>
          <CustomTableCell sx={getCellWidth('Date')}>{formatUnixTimestamp(data.enteredtimestamp)}</CustomTableCell>
          <CustomTableCellForAccount
            onClick={handleTransactionClick}
            data={data}
            accountDetails={accountDetails}
            sx={getCellWidth('Account')}
          />
          <CustomTableCell sx={getCellWidth('VoucherType')}>
            {data.transactiontype != 'OpeningBalance' ? (data.transactiontype == 'ManualJournal' ? 'Journal' : data.transactiontype) : ''}
          </CustomTableCell>
          <CustomTableCellRedirectToVoucher data={data} type={data.transactiontype} sx={getCellWidth('VoucherNumber')}>
            {data.transactiontype == 'OpeningBalance' ? '' : data.vouchernumber}
          </CustomTableCellRedirectToVoucher>
          <CustomTableCell align="right" sx={getCellWidth('debit')}>
            {data.transactiontype != 'OpeningBalance' ? formatNumber(data.totaldebitamount, false, true) : ''}
          </CustomTableCell>
          <CustomTableCell align="right" sx={getCellWidth('credit')}>
            {data.transactiontype != 'OpeningBalance' ? formatNumber(data.totalcreditamount, false, true) : ''}
          </CustomTableCell>
          <CustomTableCell align="right" sx={getCellWidth('Balance')}>
            {formatNumber(data?.balance, true, true, data.balancesign)}
          </CustomTableCell>
          {showLinkedInvoiceVsReceipt && (
            <>
              <CustomTableCell align="center" sx={getCellWidth('setOff')}>
                {canRenderSetOffButton ? (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSetOffClick}
                    sx={{
                      minWidth: '80px',
                      fontSize: '0.75rem',
                      py: 0.5,
                      px: 1,
                      ...(data?.setoffbalance === 0
                        ? { color: 'green', borderColor: 'green' }
                        : { color: '#2196f3', borderColor: '#2196f3' }),
                      ...(data?.setoffbalance === 0
                        ? {
                            '&:hover': {
                              color: 'white',
                              backgroundColor: 'green',
                              borderColor: 'green',
                            },
                          }
                        : {
                            '&:hover': {
                              color: 'white',
                              backgroundColor: '#2196f3',
                              borderColor: '#2196f3',
                            },
                          }),
                    }}
                  >
                    {data?.setoffbalance === 0 ? 'Settled' : 'Set-Off'}
                  </Button>
                ) : (
                  ''
                )}
              </CustomTableCell>
              <CustomTableCell align="right" sx={getCellWidth('PendingSetoff')}>
                {formatNumber(data?.setoffbalance || 0, false, true)}
              </CustomTableCell>
            </>
          )}
          {showLinkedInvoiceVsReceipt ? (
            <CustomTableCell align="left" sx={getCellWidth('SetoffRemarks')}>
              <Tooltip title={data?.setoffremarks || ''} arrow>
                <Box
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                  }}
                >
                  {data?.setoffremarks || ''}
                </Box>
              </Tooltip>
            </CustomTableCell>
          ) : (
            ''
          )}
          <CustomTableCell align="left" sx={getCellWidth('Narrations')}>
            <Tooltip title={data?.comments?.replace(/\*\*/g, '\n') || ''} arrow>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {data?.comments?.replace(/\*\*/g, '\n') || ''}
              </Box>
            </Tooltip>
          </CustomTableCell>
        </TableRow>
        {isOpen && (
          <SetOffModal
            open={isOpen}
            onClose={closeSetOffModal}
            voucherType={transactionData?.voucherType}
            voucherId={transactionData?.voucherId}
            voucherNumber={transactionData?.voucherNumber}
            accountid={transactionData?.accountid}
            onSetOffSuccess={() => {
              // Set localStorage flag when setoff is successful
              localStorage.setItem('ledgerTransactionSetoffDone', 'true');
              
              // Refetch data with filters preserved but reset page to 0
              if (fetchData && filters) {
                fetchData({
                  reset: true,
                  start: filters.start,
                  end: filters.end,
                  limit: 0,
                  from: rowsperpage,
                });
              }
            }}
          />
        )}
      </>
    );
  }
);

export default TableRow;
